import { useOutletContext } from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { Container } from '@/components/ui/container'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Separator } from '@/components/ui/separator'
import { ExpProPhotosGrid } from '@/features/experience-vetting/components/exp-pro-photos-grid'
import { ExpProPhotosHeader } from '@/features/experience-vetting/components/exp-pro-photos-header'
import { ExpProPhotosItineraries } from '@/features/experience-vetting/components/exp-pro-photos-itineraries'
import { ExperienceVettingCriteriaUploadButton } from '@/features/experience-vetting/components/experience-vetting-criteria-upload-button'
import { useGetExperienceVetting } from '@/features/experience-vetting/queries/use-get-experience-vetting'
import type { OpsVettingURLParams } from '@/features/ops-vetting/types'
import { ProPhotosGrid } from '@/features/submission-pro-photos-upload/components/pro-photos-grid'
import { ProPhotosSkeleton } from '@/features/submission-pro-photos-upload/components/pro-photos-skeleton'
import { ProPhotosTitle } from '@/features/submission-pro-photos-upload/components/pro-photos-title'
import { useTranslation } from '@/translations/i18n'

export const ExperienceProPhotos = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: OpsVettingURLParams }>()
	const {
		remappedData: experience,
		isPending,
		isError
	} = useGetExperienceVetting(urlParams.id)

	if (isPending || !experience) {
		return (
			<ProPhotosSkeleton
				description={t('vetting:reordering.experience.description')}
				serviceTitle={t('vetting:reordering.activities')}
				title={t('vetting:reordering.experience.title')}
			/>
		)
	}

	if (isError) {
		return (
			<Container fullWidth>
				<ErrorBox
					icon={<AlertTriangleIcon />}
					title={t('errors:default')}
				/>
			</Container>
		)
	}

	return (
		<ScrollArea scrollbar={<Scrollbar />}>
			<ExpProPhotosHeader experience={experience} />

			<ProPhotosTitle
				imagesLength={experience.gallery.images.length}
				serviceTitle={t('vetting:reordering.activities')}
				uploadButton={
					<ExperienceVettingCriteriaUploadButton
						experienceId={experience.id}
						galleryId={experience.gallery.id}
					/>
				}
			/>

			<Separator className='bg-neutral-300' orientation='horizontal' />

			<ProPhotosGrid
				leftSlot={<ExpProPhotosGrid experience={experience} />}
				rightSlot={<ExpProPhotosItineraries experience={experience} />}
			/>
		</ScrollArea>
	)
}
ExperienceProPhotos.displayName = 'ExperienceProPhotos'
